import React, { Fragment, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { alertSelector } from "../../GlobalData/Selectors";
import EventBus from "orion-components/SharedComponents/EventBus";

const synth = window.speechSynthesis;
const audioLibrary = {};

const propTypes = {
	activeAlerts: PropTypes.object,
	tts: PropTypes.object,
	isInitialBatch: PropTypes.bool
};

const defaultProps = {
	activeAlerts: {},
	tts: {
		enabled: false
	},
	isInitialBatch: true
};

const ackdAlerts = [];
const AudioAlertPlayer = () => {
	const activeAlerts = useSelector((state) => alertSelector(state)) || {};
	const acknowledgedAlerts = Object.values(activeAlerts)
		.map((alert) => (alert.occurrenceCount === alert.ackedCount) && !ackdAlerts.includes(alert.id) && alert.id)
		.filter(Boolean) || [];
	const tts = useSelector((state) => state.appState.global?.tts);

	const [voice, setVoice] = useState(null);
	const [playedIds, setPlayedIds] = useState([]);
	const [batch, setBatch] = useState([]);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLocked, setIsLocked] = useState(false);

	const activeIntervals = useRef([]);
	const batchRef = useRef(batch);
	const reopenedAlerts = useRef([]);

	useEffect(() => {
		if (!isLocked) return;
		if (!tts.enabled) {
			synth.cancel(); // stop all ongoing voice alerts
			setIsPlaying(false);
			Object.values(audioLibrary).forEach((audio) => {
				// stop all ongoing audio alerts
				audio.pause();
				audio.currentTime = 0;
			});
		}
	}, [tts, isLocked]);

	useEffect(() => {
		let releaseLock; // This will hold the resolve function for the lock promise.

		// Create a promise that will be resolved when the lock is released.
		const lockPromise = new Promise((resolve) => {
			releaseLock = resolve;
		});

		// Request an exclusive lock named "alert-audio-lock". Setting the mode to "exclusive" will ensure that only one instance of the application can hold this lock at a time.
		navigator.locks.request("audio-alerts", { mode: "exclusive" }, async () => {
			setIsLocked(true);
			await lockPromise; // Hold lock forever until releaseLock/lockPromise is resolved. This will keep the lock until the component unmounts or the lock is explicitly released.
			setIsLocked(false);
		});

		const voices = synth.getVoices();
		const voice = voices.filter((item) => item.lang === "en-US");
		setVoice(voice[0]);
		EventBus.subscribe("getAcknowledgedAlerts", getAcknowledgedAlerts);
		return () => {
			EventBus.unsubscribe("getAcknowledgedAlerts", getAcknowledgedAlerts);
			// Releasing audio lock on component unmount.
			if (releaseLock) releaseLock();
		};
	}, []);

	useEffect(() => {
		if (!isLocked) return;
		const alertsArr = Object.values(activeAlerts);
		triggerAlerts(alertsArr, tts.enabled);
		if (acknowledgedAlerts.length) getAcknowledgedAlerts({ alertsData: acknowledgedAlerts, ackAll: true });
		EventBus.subscribe("closeAlerts", handleClearInterval);
		EventBus.subscribe("reopenAlerts", handleReopenedAlerts);
	}, [activeAlerts, isLocked]);

	useEffect(() => {
		batchRef.current = batch;
		const alert = batch[0];

		if (alert && !isPlaying) {
			if (alert.audioMode === "audioFile") {
				if (!ackdAlerts.includes(alert.id)) {
					playAudio(alert);
				}
			} else {
				if (!ackdAlerts.includes(alert.id)) {
					playVoice(alert);
				}
			}
		}
	}, [batch]);

	const getAcknowledgedAlerts = (data) => {
		const { alertsData, ackAll } = data;

		if (!ackAll) {
			const { id, audioSettings } = alertsData;

			if (!ackdAlerts.includes(id)) ackdAlerts.push(id);

			if (audioSettings.audioMode === "audioFile" && audioLibrary[id]) {
				const alertAudio = audioLibrary[id];
				alertAudio.pause();
				alertAudio.currentTime = 0;
				delete audioLibrary[id];
			}
		} else {
			alertsData.forEach((alertId) => {
				// Acknowledging all alerts
				if (!ackdAlerts.includes(alertId)) ackdAlerts.push(alertId);

				synth.cancel(); // Stop all ongoing voice alerts
				setIsPlaying(false);

				if (audioLibrary[alertId]) {
					const alertAudio = audioLibrary[alertId];
					alertAudio.pause();
					alertAudio.currentTime = 0;
					delete audioLibrary[alertId];
				}
			});
		}
	};

	// Add alert to array of played ids
	const setAlertPlayed = (id) => {
		setPlayedIds([...playedIds, id]);
	};

	const handleClearInterval = (closedAlerts) => {
		const dismissIds = [];
		activeIntervals.current.map(
			(interval) => closedAlerts.includes(interval.alertId) && dismissIds.push(interval.intervalId)
		);
		setBatch((prevBatch) => prevBatch.filter((item) => !closedAlerts.includes(item.id)));

		if (dismissIds.length > 0) {
			dismissIds.map((dismissId) => {
				synth.cancel(); // stop all ongoing voice alerts
				setIsPlaying(false);
				Object.values(audioLibrary).forEach((audio) => {
					// stop all ongoing audio alerts
					audio.pause();
					audio.currentTime = 0;
				});
				clearTimeout(dismissId);
				EventBus.unsubscribe("closeAlerts", handleClearInterval);
				const filtered = activeIntervals.current.filter((item) => item.intervalId !== dismissId);
				activeIntervals.current = filtered;
			});
		}
		setIsPlaying(false);
	};

	const handleReopenedAlerts = (reopenedIds) => {
		reopenedAlerts.current = [...reopenedAlerts.current, reopenedIds];
		EventBus.unsubscribe("reopenAlerts", handleReopenedAlerts);
	};

	const addBatch = (alert, initial) => {
		const isActive = batchRef.current.find((item) => item.id === alert.id);

		if (isActive) {
			return;
		}
		// reset alert batch when a new alert enters, to keep the recent alert on top.
		if (initial) {
			synth.cancel();
			Object.values(audioLibrary).forEach((audio) => {
				audio.pause();
				audio.currentTime = 0;
			});

			// reset all active timeouts to restrict audio overlapping.
			activeIntervals.current.map((interval) => clearTimeout(interval.intervalId));

			setIsPlaying(false);
			setBatch((prevBatch) => {
				const newBatch = [...prevBatch];
				newBatch.unshift(alert);
				return newBatch;
			});
		} else {
			setBatch((prevBatch) => {
				const newBatch = [...prevBatch];
				newBatch.push(alert);
				return newBatch;
			});
		}
	};

	// Render fragment components for each alert that needs to be played
	const triggerAlerts = (alerts, enabled) => {
		if (alerts.length) {
			// Set initial alerts as played
			if (!global.isInitialAlertsLoaded) {
				const ids = alerts.map((item) => item.id);
				setPlayedIds(ids);
				global.isInitialAlertsLoaded = true;
			} else {
				// Alerts that have not been marked as played
				const filteredAlerts = alerts.filter(
					(item) => !playedIds.includes(item.id) && !reopenedAlerts.current.includes(item.id)
				);

				// If audio alerts are not enabled, mark all incoming alerts as played
				if (!enabled) {
					filteredAlerts.forEach((item) => {
						setAlertPlayed(item.id);
					});
				}
				// Otherwise, if alerts are to be played, play them
				else if (filteredAlerts.length) {
					return playAlerts(filteredAlerts);
				}
			}
		} else {
			synth.cancel(); // stop all ongoing voice alerts
			setIsPlaying(false);
			setBatch([]);
		}

		return null;
	};

	const playAlerts = (filteredAlerts) => {
		const alertText = filteredAlerts.map((alert) => {
			let alertStr = "";
			const audioMode = alert?.audioSettings?.audioMode;
			const loopAudio = (audioMode && alert?.audioSettings[audioMode]?.loopAudio) ?? false;
			const loopInterval = (audioMode && alert?.audioSettings[audioMode]?.loopInterval) ?? 5;
			const soundFile = (audioMode && alert?.audioSettings?.soundFile) ?? "";

			if (alert.audioSettings) {
				if (alert.audioSettings.speakAlertText)
					alertStr += alert.audioSettings.alertText ? alert.audioSettings.alertText : "Alert, Alert";
				if (alert.audioSettings.speakAlertNotification) alertStr += " " + alert.message;
			} else {
				alertStr = "Alert, Alert " + alert.message;
			}
			return {
				text: alertStr,
				id: alert.id,
				audioMode,
				soundFile,
				loopAudio: loopAudio,
				loopInterval: loopInterval
			};
		});

		if (alertText.length) {
			const data = alertText.map((alert) => {
				if (alert.audioMode === "audioFile") {
					addBatch(
						{
							...alert,
							src: `${window.location.origin}/_fileDownload?bucketName=notification-sound-files&fileName=${alert.soundFile}`
						},
						true
					);
				} else {
					addBatch(alert, true);
				}
			});
			return data;
		}
	};

	const playAudio = (alert) => {
		const fileName = alert.soundFile.replace(/\.\w+$/, "");

		// getting audio player from cached sources
		let audio;
		if (!audioLibrary[fileName]) {
			audio = new Audio(alert.src);
			audioLibrary[alert.id] = audio;
		} else {
			audio = audioLibrary[fileName];
		}

		// Check if audio player is idle, before playing audio instance
		if (!isPlaying) {
			setIsPlaying(true);
			audio.play();
		}

		const eventListener = () => {
			setIsPlaying(false); // re-set audio player
			setBatch((prevBatch) => prevBatch.filter((item) => item.id !== alert.id)); //remove played alerts from batch array
			if (alert.loopAudio) {
				const intervalId = setTimeout(() => addBatch(alert), alert.loopInterval * 1000);
				activeIntervals.current = [...activeIntervals.current, { alertId: alert.id, intervalId }];
			}

			audio.removeEventListener("ended", eventListener);
		};

		audio.addEventListener("ended", eventListener);
	};

	const playVoice = (alert) => {
		const audio = new SpeechSynthesisUtterance(alert.text);
		audio.voice = voice;

		if (!isPlaying) {
			setIsPlaying(true);
			synth.speak(audio);
		}

		// End handler
		audio.onend = () => {
			setAlertPlayed(alert.id);
			setIsPlaying(false); // re-set audio player
			setBatch((prevBatch) => prevBatch.filter((item) => item.id !== alert.id)); //remove played alerts from batch array
			if (alert.loopAudio) {
				const intervalId = setTimeout(() => addBatch(alert), alert.loopInterval * 1000);
				activeIntervals.current = [...activeIntervals.current, { alertId: alert.id, intervalId }];
			}
		};
	};

	return <Fragment />;
};

AudioAlertPlayer.propTypes = propTypes;
AudioAlertPlayer.defaultProps = defaultProps;

export default AudioAlertPlayer;
